import classNames from 'classnames';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import {
  initialValuesSmsf,
  validationSchemaSmsf,
} from '@/modules/entity/components/smsf/SmsfDetails.tsx';
import {
  ShareholderDetails,
  SmsfEntityDetails,
} from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import {
  Button,
  CustomCheckbox,
  RadioButton,
  Select,
  SelectButtons,
  SelectOption,
  TextInputField,
} from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { PlusBlueIcon, TrashIcon } from 'ui/icons';
import { UpdateEntityRequest } from 'api';

const radioButtonItems = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

const stakeholderTypeOptions = [
  { label: 'Individual', value: 'individual' },
  { label: 'Company', value: 'company' },
];

export const SmsfDetailsHolder = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const getInitialValues = (): SmsfEntityDetails => {
    const country =
      dataHolder?.data?.new_data?.entity_data?.smsf_entity_details?.country ||
      dataHolder?.data?.new_data?.entity_data?.country ||
      initialValuesSmsf.country;
    return {
      ...initialValuesSmsf,
      country,
      ...dataHolder?.data?.new_data?.entity_data?.smsf_entity_details,
      settlors: dataHolder?.data?.new_data?.entity_data?.smsf_entity_details
        ? (dataHolder?.data?.new_data?.entity_data?.smsf_entity_details?.settlors ?? [])?.map(
            item => ({
              ...item,
              type: item.company_name ? 'company' : 'individual',
              index: item.index || uuidv4(),
            }),
          )
        : initialValuesSmsf?.settlors,
      beneficiaries: dataHolder?.data?.new_data?.entity_data?.smsf_entity_details
        ? (dataHolder?.data?.new_data?.entity_data?.smsf_entity_details?.beneficiaries ?? [])?.map(
            item => ({
              ...item,
              type: item.company_name ? 'company' : 'individual',
              index: item.index || uuidv4(),
            }),
          )
        : initialValuesSmsf?.beneficiaries,
      trustee: dataHolder?.data?.new_data?.entity_data?.smsf_entity_details
        ? (dataHolder?.data?.new_data?.entity_data?.smsf_entity_details?.trustee ?? [])?.map(
            item => ({
              ...item,
              type: item.company_name ? 'company' : 'individual',
              index: item.index || uuidv4(),
            }),
          )
        : initialValuesSmsf?.trustee,
    };
  };

  const formik = useFormik<SmsfEntityDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaSmsf,
    initialValues: getInitialValues(),
    onSubmit: async ({ company_name, ...details }) => {
      setDirty(false);
      if (formik.dirty) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            entity_name: company_name,
            smsf_entity_details: {
              ...dataHolder?.data?.new_data?.entity_data?.smsf_entity_details,
              ...details,
              company_name,
            },
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeSelect = (field: string, value: string | boolean) => {
    formik.setFieldValue(`${field}`, value);
    formik.setFieldError(`${field}`, '');
  };

  const handleDeleteBeneficiary = (index: string) => {
    formik.setFieldValue('beneficiaries', [
      ...(formik.values.beneficiaries || []).filter(item => item.index !== index),
    ]);
  };

  const handleAddBeneficiary = () => {
    formik.setFieldValue('beneficiaries', [
      ...(formik.values.beneficiaries || []),
      {
        type: 'individual',
        first_name: '',
        last_name: '',
        index: uuidv4(),
        same_as: false,
        same_as_index: '',
      },
    ]);
    formik.setFieldError('beneficiaries', '');
  };

  const handleDeleteTrustee = (index: string) => {
    formik.setFieldValue('trustee', [
      ...(formik.values.trustee || []).filter(item => item.index !== index),
    ]);
  };

  const handleAddTrustee = () => {
    formik.setFieldValue('trustee', [
      ...(formik.values.trustee || []),
      {
        type: 'individual',
        first_name: '',
        last_name: '',
        index: uuidv4(),
        same_as: false,
        same_as_index: '',
      },
    ]);
    formik.setFieldError('trustee', '');
  };

  const handleDeleteSettlor = (index: string) => {
    formik.setFieldValue('settlors', [
      ...(formik.values.settlors || []).filter(item => item.index !== index),
    ]);
  };

  const handleAddSettlor = () => {
    formik.setFieldValue('settlors', [
      ...(formik.values.settlors || []),
      {
        type: 'individual',
        first_name: '',
        last_name: '',
        index: uuidv4(),
        same_as: false,
        same_as_index: '',
      },
    ]);
    formik.setFieldError('settlors', '');
  };

  const listAllStakeholdersByIndex = useMemo<SelectOption<ShareholderDetails>[]>(() => {
    const trustee: SelectOption<ShareholderDetails>[] =
      formik?.values?.trustee?.map(item => ({
        value: item,
        label: item.index,
      })) || [];
    const beneficiaries: SelectOption<ShareholderDetails>[] =
      formik?.values?.beneficiaries?.map(item => ({
        value: item,
        label: item.index,
      })) || [];
    const settlors: SelectOption<ShareholderDetails>[] =
      formik?.values?.settlors?.map(item => ({
        value: item,
        label: item.index,
      })) || [];
    return [...trustee, ...beneficiaries, ...settlors];
  }, [formik.values]);

  const getAnotherStakeholders = useCallback(
    (excludeIndex: string[]): SelectOption<string>[] => {
      const trustee: SelectOption<string>[] =
        formik?.values?.trustee
          ?.filter(item => !excludeIndex?.includes(item?.index) && !item?.same_as)
          ?.map(item => ({
            value: item.index,
            label: `Trustee #${
              (formik?.values?.trustee || [])?.map(item => item.index)?.indexOf(item.index) + 1
            }`,
          })) || [];
      const beneficiaries: SelectOption<string>[] =
        formik?.values?.beneficiaries
          ?.filter(item => !excludeIndex?.includes(item?.index) && !item?.same_as)
          ?.map(item => ({
            value: item.index,
            label: `Beneficiaries #${
              (formik?.values?.beneficiaries || [])?.map(item => item.index)?.indexOf(item.index) +
              1
            }`,
          })) || [];
      const settlors: SelectOption<string>[] =
        formik?.values?.settlors
          ?.filter(item => !excludeIndex?.includes(item?.index) && !item?.same_as)
          ?.map(item => ({
            value: item.index,
            label: `Settlors #${
              (formik?.values?.settlors || [])?.map(item => item.index)?.indexOf(item.index) + 1
            }`,
          })) || [];
      return [...trustee, ...beneficiaries, ...settlors];
    },
    [formik?.values],
  );

  useEffect(() => {
    console.log('errors', formik.errors);
  }, [formik.errors]);

  return (
    <OnboardingHolderWrapper
      title="Self Managed Superannuation Fund details"
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col gap-8">
        <TextInputField label="name" name="company_name" />
        {formik.values.country === 'AUS' && <TextInputField label="abn" name="abn" />}
        <TextInputField
          label="Registered Office Address"
          name="registered_office"
          placeholder="111/11 Name Street, North Sydney NSW 1111"
        />
        <TextInputField
          label="Company/Business Name of Trustee"
          name="business_name_trustee"
          onChange={e => {
            const value = e.target.value;
            formik.setFieldValue('business_name_trustee', value);
            formik.setFieldError('business_name_trustee', '');
            if (formik.values?.trustee?.[0]?.type === 'company') {
              formik.setFieldValue('trustee[0].company_name', value);
              formik.setFieldError('trustee[0].company_name', '');
            }
          }}
        />
        {/*  */}
        <div className="w-full flex flex-col gap-6">
          <div className="flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2 w-full">
            <span className="text-[16px] leading-6 font-[500] font-[Inter]">
              Tell us about your trustees
            </span>
            <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddTrustee}>
              <img src={PlusBlueIcon} alt="Loading" />
              <span className="text-[14px] leading-[22px]">Add new trustee</span>
            </Button>
          </div>
          <div className="w-full flex flex-col gap-6">
            {formik.values.trustee?.map((item, index, all) => {
              const sameAs = listAllStakeholdersByIndex.find(
                stakeholder => stakeholder.value.index === item.same_as_index && item.same_as,
              );
              const itemChose = !!listAllStakeholdersByIndex
                ?.filter(b => !all?.map(a => a.index).includes(b.value.index))
                ?.find(a => a.value.same_as_index === item.index);
              const anotherStakeholders = getAnotherStakeholders([
                ...all.map(a => a.index ?? ''),
                ...all.map(a => a.same_as_index ?? '').filter((_, i) => i !== index),
                item.index,
              ]);
              return (
                <div key={'trustee_' + index} className="w-full flex flex-col gap-4">
                  <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2">
                    <span className="font-[500] font-[Inter] text-sm leading-[22px]">
                      Trustee #{index + 1}
                    </span>
                    <div className="flex gap-4 items-center select-none">
                      {!itemChose && anotherStakeholders.length > 0 && (
                        <span className="flex gap-4 items-center">
                          <CustomCheckbox
                            className="w-auto"
                            label={item.same_as ? 'Same as:' : 'Same as'}
                            checked={item.same_as}
                            onChange={e => {
                              if (!e.target.checked) {
                                formik.setFieldValue(`trustee[${index}].same_as_index`, '');
                              }
                              handleChangeSelect(`trustee[${index}].same_as`, e.target.checked);
                            }}
                          />
                          <span
                            className={classNames('text-sm leading-5 font-[Inter] w-[190px]', {
                              hidden: !item.same_as,
                            })}
                          >
                            <Select
                              options={anotherStakeholders}
                              value={item.same_as_index}
                              onChange={value => {
                                formik.setFieldValue(`trustee[${index}].type`, 'individual');
                                formik.setFieldError(`trustee[${index}].type`, '');
                                formik.setFieldValue(`trustee[${index}].first_name`, '');
                                formik.setFieldValue(`trustee[${index}].last_name`, '');
                                formik.setFieldValue(`trustee[${index}].company_name`, '');
                                formik.setFieldError(`trustee[${index}].first_name`, '');
                                formik.setFieldError(`trustee[${index}].last_name`, '');
                                formik.setFieldError(`trustee[${index}].company_name`, '');
                                handleChangeSelect(`trustee[${index}].same_as_index`, value || '');
                              }}
                              withBorder={false}
                              showBorderBottom={false}
                              disabled={!item.same_as}
                              bgTransparent={true}
                              isClearable={true}
                            />
                          </span>
                        </span>
                      )}

                      <img
                        onClick={() => handleDeleteTrustee(item.index)}
                        className="cursor-pointer hover:opacity-80"
                        src={TrashIcon}
                        alt="Loading..."
                      />
                    </div>
                  </div>

                  <SelectButtons<string>
                    label="Do you have individual or a corporate Trustee?"
                    options={stakeholderTypeOptions}
                    selected={findDefaultValue(
                      stakeholderTypeOptions,
                      sameAs?.value?.type ?? item.type,
                    )}
                    onChange={value => {
                      if (value === 'individual') {
                        formik.setFieldValue(`trustee[${index}].company_name`, '');
                        formik.setFieldError(`trustee[${index}].company_name`, '');
                      }
                      if (value === 'company') {
                        formik.setFieldValue(`trustee[${index}].first_name`, '');
                        formik.setFieldValue(`trustee[${index}].last_name`, '');
                        formik.setFieldError(`trustee[${index}].first_name`, '');
                        formik.setFieldError(`trustee[${index}].last_name`, '');
                        if (formik.values.business_name_trustee && index === 0) {
                          formik.setFieldValue(
                            `trustee[${index}].company_name`,
                            formik.values.business_name_trustee,
                          );
                          formik.setFieldError(`trustee[${index}].company_name`, '');
                        }
                      }
                      handleChangeSelect(`trustee[${index}].type`, value || '');
                    }}
                    disabled={!!sameAs}
                  />
                  <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between sm:gap-4 gap-2">
                    {(sameAs?.value?.type || item.type) === 'individual' && (
                      <>
                        <TextInputField
                          label="first name"
                          name={`trustee[${index}].first_name`}
                          value={sameAs?.value?.first_name}
                          disabled={!!sameAs}
                        />
                        <TextInputField
                          label="last name"
                          name={`trustee[${index}].last_name`}
                          value={sameAs?.value?.last_name}
                          disabled={!!sameAs}
                        />
                      </>
                    )}
                    {(sameAs?.value?.type || item.type) === 'company' && (
                      <TextInputField
                        label="name"
                        name={`trustee[${index}].company_name`}
                        value={sameAs?.value?.company_name}
                        onChange={e => {
                          const value = e.target.value;
                          formik.setFieldValue(`trustee[${index}].company_name`, value);
                          formik.setFieldError(`trustee[${index}].company_name`, '');
                          if (index === 0) {
                            formik.setFieldValue('business_name_trustee', value);
                            formik.setFieldError('business_name_trustee', '');
                          }
                        }}
                        disabled={!!sameAs}
                      />
                    )}
                  </div>
                </div>
              );
            })}
            {typeof formik.errors?.trustee === 'string' && formik.errors?.trustee && (
              <span className="text-sm text-red-500">{formik.errors?.trustee}</span>
            )}
          </div>
        </div>

        <div className="w-full flex flex-col space-y-6">
          <div className="flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2 w-full">
            <span className="text-[16px] leading-6 font-[500] font-[Inter]">
              Tell us about your beneficiaries
            </span>
            <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddBeneficiary}>
              <img src={PlusBlueIcon} alt="Loading" />
              <span className="text-[14px] leading-[22px]">Add new beneficiary</span>
            </Button>
          </div>
          <div className="w-full flex flex-col gap-6">
            {formik.values.beneficiaries?.map((item, index, all) => {
              const sameAs = listAllStakeholdersByIndex.find(
                stakeholder => stakeholder.value.index === item.same_as_index && item.same_as,
              );
              const itemChose = !!listAllStakeholdersByIndex
                ?.filter(b => !all?.map(a => a.index).includes(b.value.index))
                ?.find(a => a.value.same_as_index === item.index);
              const anotherStakeholders = getAnotherStakeholders([
                ...all.map(a => a.index ?? ''),
                ...all.map(a => a.same_as_index ?? '').filter((_, i) => i !== index),
                item.index,
              ]);
              return (
                <div key={'beneficiaries_' + index} className="w-full flex flex-col gap-4">
                  <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2">
                    <span className="font-[500] font-[Inter] text-sm leading-[22px]">
                      Beneficiary #{index + 1}
                    </span>
                    <div className="flex gap-4 items-center select-none">
                      {!itemChose && anotherStakeholders.length > 0 && (
                        <span className="flex gap-4 items-center">
                          <CustomCheckbox
                            className="w-auto"
                            label={item.same_as ? 'Same as:' : 'Same as'}
                            checked={item.same_as}
                            onChange={e => {
                              if (!e.target.checked) {
                                formik.setFieldValue(`beneficiaries[${index}].same_as_index`, '');
                              }
                              handleChangeSelect(
                                `beneficiaries[${index}].same_as`,
                                e.target.checked,
                              );
                            }}
                          />
                          <span
                            className={classNames('text-sm leading-5 font-[Inter] w-[190px]', {
                              hidden: !item.same_as,
                            })}
                          >
                            <Select
                              options={anotherStakeholders}
                              value={item.same_as_index}
                              onChange={value => {
                                formik.setFieldValue(`beneficiaries[${index}].type`, 'individual');
                                formik.setFieldError(`beneficiaries[${index}].type`, '');
                                formik.setFieldValue(`beneficiaries[${index}].first_name`, '');
                                formik.setFieldValue(`beneficiaries[${index}].last_name`, '');
                                formik.setFieldValue(`beneficiaries[${index}].company_name`, '');
                                formik.setFieldError(`beneficiaries[${index}].first_name`, '');
                                formik.setFieldError(`beneficiaries[${index}].last_name`, '');
                                formik.setFieldError(`beneficiaries[${index}].company_name`, '');
                                handleChangeSelect(
                                  `beneficiaries[${index}].same_as_index`,
                                  value || '',
                                );
                              }}
                              withBorder={false}
                              showBorderBottom={false}
                              disabled={!item.same_as}
                              bgTransparent={true}
                              isClearable={true}
                            />
                          </span>
                        </span>
                      )}

                      {(index !== 0 || all.length > 1) && (
                        <img
                          onClick={() => {
                            handleDeleteBeneficiary(item.index);
                          }}
                          className="cursor-pointer hover:opacity-80"
                          src={TrashIcon}
                          alt="Loading..."
                        />
                      )}
                    </div>
                  </div>

                  <SelectButtons<string>
                    label="Do you have individual or a corporate Beneficiary?"
                    options={stakeholderTypeOptions}
                    selected={findDefaultValue(
                      stakeholderTypeOptions,
                      sameAs?.value?.type ?? item.type,
                    )}
                    onChange={value => {
                      if (value === 'individual') {
                        formik.setFieldValue(`beneficiaries[${index}].company_name`, '');
                        formik.setFieldError(`beneficiaries[${index}].company_name`, '');
                      }
                      if (value === 'company') {
                        formik.setFieldValue(`beneficiaries[${index}].first_name`, '');
                        formik.setFieldValue(`beneficiaries[${index}].last_name`, '');
                        formik.setFieldError(`beneficiaries[${index}].first_name`, '');
                        formik.setFieldError(`beneficiaries[${index}].last_name`, '');
                      }
                      handleChangeSelect(`beneficiaries[${index}].type`, value || '');
                    }}
                    disabled={!!sameAs}
                  />
                  <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between sm:gap-4 gap-2">
                    {(sameAs?.value?.type || item.type) === 'individual' && (
                      <>
                        <TextInputField
                          label="first name"
                          name={`beneficiaries[${index}].first_name`}
                          value={sameAs?.value?.first_name}
                          disabled={!!sameAs}
                        />
                        <TextInputField
                          label="last name"
                          name={`beneficiaries[${index}].last_name`}
                          value={sameAs?.value?.last_name}
                          disabled={!!sameAs}
                        />
                      </>
                    )}
                    {(sameAs?.value?.type || item.type) === 'company' && (
                      <TextInputField
                        label="name"
                        name={`beneficiaries[${index}].company_name`}
                        value={sameAs?.value?.company_name}
                        disabled={!!sameAs}
                      />
                    )}
                  </div>
                </div>
              );
            })}
            {typeof formik.errors?.beneficiaries === 'string' && formik.errors?.beneficiaries && (
              <span className="text-sm text-red-500">{formik.errors?.beneficiaries}</span>
            )}
          </div>
        </div>

        {(formik.values.settlors || [])?.length === 0 && (
          <div className="w-full flex flex-col">
            <p className="text-[16px] leading-6 pb-4">Does this trust have settlors?</p>
            <RadioButton
              items={radioButtonItems}
              value={false}
              onChange={() => handleAddSettlor()}
            />
          </div>
        )}

        {(formik.values.settlors || [])?.length > 0 && (
          <div className="w-full flex flex-col gap-6">
            <div className="flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2 w-full">
              <span className="text-[16px] leading-6 font-[500] font-[Inter]">
                Tell us about your settlors
              </span>
              <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddSettlor}>
                <img src={PlusBlueIcon} alt="Loading" />
                <span className="text-[14px] leading-[22px]">Add new settlor</span>
              </Button>
            </div>
            <div className="w-full flex flex-col gap-6">
              {formik.values.settlors?.map((item, index, all) => {
                const sameAs = listAllStakeholdersByIndex.find(
                  stakeholder => stakeholder.value.index === item.same_as_index && item.same_as,
                );
                const itemChose = !!listAllStakeholdersByIndex
                  ?.filter(b => !all?.map(a => a.index).includes(b.value.index))
                  ?.find(a => a.value.same_as_index === item.index);
                const anotherStakeholders = getAnotherStakeholders([
                  ...all.map(a => a.index ?? ''),
                  ...all.map(a => a.same_as_index ?? '').filter((_, i) => i !== index),
                  item.index,
                ]);
                return (
                  <div key={'settlor_' + index} className="w-full flex flex-col gap-4">
                    <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2">
                      <span className="font-[500] font-[Inter] text-sm leading-[22px]">
                        Settlor #{index + 1}
                      </span>
                      <div className="flex gap-4 items-center select-none">
                        {!itemChose && anotherStakeholders.length > 0 && (
                          <span className="flex gap-4 items-center">
                            <CustomCheckbox
                              className="w-auto"
                              label={'Same as:'}
                              checked={item.same_as}
                              onChange={e => {
                                if (!e.target.checked) {
                                  formik.setFieldValue(`settlors[${index}].same_as_index`, '');
                                }
                                handleChangeSelect(`settlors[${index}].same_as`, e.target.checked);
                              }}
                            />
                            <span className="text-sm leading-5 font-[Inter] w-[190px]">
                              <Select
                                options={anotherStakeholders}
                                value={item.same_as_index}
                                onChange={value => {
                                  formik.setFieldValue(`settlors[${index}].type`, 'individual');
                                  formik.setFieldError(`settlors[${index}].type`, '');
                                  formik.setFieldValue(`settlors[${index}].first_name`, '');
                                  formik.setFieldValue(`settlors[${index}].last_name`, '');
                                  formik.setFieldValue(`settlors[${index}].company_name`, '');
                                  formik.setFieldError(`settlors[${index}].first_name`, '');
                                  formik.setFieldError(`settlors[${index}].last_name`, '');
                                  formik.setFieldError(`settlors[${index}].company_name`, '');
                                  handleChangeSelect(
                                    `settlors[${index}].same_as_index`,
                                    value || '',
                                  );
                                }}
                                withBorder={false}
                                showBorderBottom={false}
                                disabled={!item.same_as}
                                bgTransparent={true}
                                isClearable={true}
                              />
                            </span>
                          </span>
                        )}

                        <img
                          onClick={() => {
                            handleDeleteSettlor(item.index);
                          }}
                          className="cursor-pointer hover:opacity-80"
                          src={TrashIcon}
                          alt="Loading..."
                        />
                      </div>
                    </div>

                    <SelectButtons<string>
                      label="Do you have individual or a corporate Settlor?"
                      options={stakeholderTypeOptions}
                      selected={findDefaultValue(
                        stakeholderTypeOptions,
                        sameAs?.value?.type ?? item.type,
                      )}
                      onChange={value => {
                        if (value === 'individual') {
                          formik.setFieldValue(`settlors[${index}].company_name`, '');
                          formik.setFieldError(`settlors[${index}].company_name`, '');
                        }
                        if (value === 'company') {
                          formik.setFieldValue(`settlors[${index}].first_name`, '');
                          formik.setFieldValue(`settlors[${index}].last_name`, '');
                          formik.setFieldError(`settlors[${index}].first_name`, '');
                          formik.setFieldError(`settlors[${index}].last_name`, '');
                        }
                        handleChangeSelect(`settlors[${index}].type`, value || '');
                      }}
                      disabled={!!sameAs}
                    />
                    <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between sm:gap-4 gap-2">
                      {(sameAs?.value?.type || item.type) === 'individual' && (
                        <>
                          <TextInputField
                            label="first name"
                            name={`settlors[${index}].first_name`}
                            value={sameAs?.value?.first_name}
                            disabled={!!sameAs}
                          />
                          <TextInputField
                            label="last name"
                            name={`settlors[${index}].last_name`}
                            value={sameAs?.value?.last_name}
                            disabled={!!sameAs}
                          />
                        </>
                      )}
                      {(sameAs?.value?.type || item.type) === 'company' && (
                        <TextInputField
                          label="name"
                          name={`settlors[${index}].company_name`}
                          value={sameAs?.value?.company_name}
                          disabled={!!sameAs}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              {typeof formik.errors?.settlors === 'string' && formik.errors?.settlors && (
                <span className="text-sm text-red-500">{formik.errors?.settlors}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </OnboardingHolderWrapper>
  );
};
